import {
  type DatasourceDataRequest,
  type DatasourceDetailsRequest,
} from '@atlaskit/linking-types';

import { DatasourceClient } from '@canvas/atlassian-client/datasource';
import { retryWithBackoff } from '@canvas/retry';

import { SiteService } from '../site-service';

import { getDatasourceConfig, type DatasourceType } from './datasource-config';
export { type DatasourceType } from './datasource-config';

const fetcher = async (input: RequestInfo, init?: RequestInit) =>
  await retryWithBackoff({
    callback: () => globalThis.fetch(input, init),
    maxRetries: 3,
  });

export class DatasourceService {
  public constructor(
    private datasourceType: DatasourceType,
    private client = new DatasourceClient(
      fetcher,
      new URL('/gateway/api/object-resolver', globalThis.location.origin)
    )
  ) {}

  private getDatasourceConfig() {
    return getDatasourceConfig(this.datasourceType);
  }

  public getData(params: DatasourceDataRequest) {
    const { datasourceId } = this.getDatasourceConfig();
    return this.client.getData(datasourceId, params);
  }

  public async getAvailableSites() {
    const { productName } = this.getDatasourceConfig();
    const siteService = SiteService.getInstance();
    const availableSites = siteService.getSites(productName);
    if (availableSites.length === 0) {
      await siteService.loadSites();
      return siteService.getSites(productName);
    }
    return availableSites;
  }

  public async getSupportedProperties(params: DatasourceDetailsRequest) {
    const { datasourceId } = this.getDatasourceConfig();
    const response = await this.client.getDetails(datasourceId, params);
    return response?.data?.schema?.properties ?? [];
  }

  public getId() {
    const { datasourceId } = this.getDatasourceConfig();
    return datasourceId;
  }
}
